import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import { useUser } from 'builder/hooks/useUser'
import { getDefaultLogoForCompany } from 'builder/components/FindJob/utils/getDefaultLogoForCompany'
import { Skeleton } from 'builder/Skeleton/Skeleton'
import { selectors } from 'builder/modules/interview/interviewModule'
import PerformanceLogger from 'builder/services/PerformanceLogger'
import { WholeConversationFeedbackCategory } from 'builder/modules/interview/types'

import { NavbarWithMenu } from 'builder/views/Interview/components/Navbar/NavbarWithMenu/NavbarWithMenu'
import { useInterview } from '../../hooks/useInterview'
import { formatSeconds } from '../../utils/formatSeconds'
import {
  Container,
  Title,
  Logo,
  JobTitle,
  Content,
  Duration,
  GeneralFeedbackLeft,
  GeneralFeedbackContainer,
  GeneralFeedbackRight,
  GeneralFeedbackTitle,
  Paywall,
  PaywallText,
  RejectionSection,
  RejectionContent,
  RejectionTitle,
  RejectionBody,
  GeneralFeedbackText,
} from './styles'
import QuestionPanel from './components/QuestionPanel/QuestionPanel'
import LockIcon from './assets/LockIcon'
import { BtnUpgrade } from './components/BtnUpgrade/BtnUpgrade'
import { ShowStyledHTML } from './components/ShowStyledHTML/ShowStyledHTML'

export const TRANSLATION_PREFIX = `builder.interview_prep_view.feedbackV2`

export const FeedbackView = () => {
  const user = useUser()
  const { i18n } = useI18n()

  const interviewId = useSelector(selectors.interviewId)
  const { data: interviewReadyData } = useInterview({ interviewId })
  const interview = interviewReadyData?.payload

  const navigate = useNavigate()

  const rootRef = useRef<HTMLDivElement | null>(null)

  const stopPerformance = useCallback(() => {
    PerformanceLogger.finish()
  }, [])

  useEffect(() => {
    if (!interviewId) {
      stopPerformance()
      navigate('/interview-preparation/dashboard')
      return
    }

    trackInternalEvent('view_interview_report', { label: 'interview_prep' })
    return () => {
      stopPerformance()
    }
  }, [])

  const isPremium = !!user?.hasPremiumFeatures

  const sortFeedback: WholeConversationFeedbackCategory[] = useMemo(() => {
    const feedBackCategory =
      interview?.conversation_feedback?.whole_conversation_feedback_category || []
    const keyword = feedBackCategory.find(i => i.criterion === 'keyword')

    const nonKeyWordCriteria = feedBackCategory.filter(i => i.criterion !== 'keyword')

    if (keyword) {
      nonKeyWordCriteria.unshift(keyword)
    }

    return nonKeyWordCriteria
  }, [interview?.conversation_feedback?.whole_conversation_feedback_category])

  const firstName = (user?.firstName || '').trim()
  return (
    <Container ref={rootRef}>
      <NavbarWithMenu
        onCloseClick={() => navigate('/interview-preparation/dashboard')}
        onDashboardClick={() => navigate('/interview-preparation/dashboard')}
      />

      {interview ? (
        <Title>
          {`${i18n.t(`${TRANSLATION_PREFIX}.phrase1_1`)}${
            firstName === '' ? '' : ' ' + firstName
          }, ${i18n.t(`${TRANSLATION_PREFIX}.phrase1_2`)}`}
          <br />
          {i18n.t(`${TRANSLATION_PREFIX}.phrase1_3`)}
          <Logo>
            <img src={interview.logo_url || getDefaultLogoForCompany()} alt="logo" />
          </Logo>
          <JobTitle>
            {interview?.job_title}&nbsp;
            {interview?.real_company &&
              interview?.company_name &&
              `${i18n.t(`${TRANSLATION_PREFIX}.at_preposition`)} ${interview?.company_name} `}
            {i18n.t(`${TRANSLATION_PREFIX}.interview`)}
          </JobTitle>
        </Title>
      ) : (
        <Title>
          <Skeleton width={674} height={34} />
          <Skeleton width={779} height={34} style={{ marginTop: '5px', marginBottom: '66px' }} />
        </Title>
      )}
      <Content>
        {interview?.conversation_feedback?.whole_conversation_feedback && (
          <GeneralFeedbackContainer>
            <GeneralFeedbackLeft>
              <GeneralFeedbackTitle>
                {i18n.t(`${TRANSLATION_PREFIX}.general_feedback.title`)}
              </GeneralFeedbackTitle>
              <Duration>{formatSeconds(interview?.total_duration || 0)}</Duration>
            </GeneralFeedbackLeft>
            {interview?.conversation_feedback?.whole_conversation_feedback && (
              <GeneralFeedbackRight>
                <p>
                  <GeneralFeedbackText
                    dangerouslySetInnerHTML={{
                      __html: interview.conversation_feedback.whole_conversation_feedback,
                    }}
                  />
                </p>
                {sortFeedback?.length && sortFeedback?.length > 0 && (
                  <RejectionSection>
                    <RejectionTitle>
                      {i18n.t(`${TRANSLATION_PREFIX}.general_feedback.avoid_rejection`)}
                    </RejectionTitle>
                    <RejectionContent>
                      {(isPremium ? sortFeedback : [sortFeedback[0]]).map(
                        (feedback: WholeConversationFeedbackCategory) => (
                          <RejectionBody
                            key={feedback.criterion}
                            data-criterion={feedback.criterion}
                          >
                            <RejectionTitle>
                              {i18n.t(
                                `${TRANSLATION_PREFIX}.general_feedback.${feedback.criterion}`,
                              )}
                              :{' '}
                            </RejectionTitle>

                            <ShowStyledHTML
                              dangerouslySetInnerHTML={{ __html: feedback.content }}
                            />
                          </RejectionBody>
                        ),
                      )}
                      {!isPremium && (
                        <Paywall>
                          <LockIcon />
                          <PaywallText>
                            {i18n.t(`${TRANSLATION_PREFIX}.general_feedback.paywall_text`)}
                          </PaywallText>
                          <BtnUpgrade section="feedback" />
                        </Paywall>
                      )}
                    </RejectionContent>
                  </RejectionSection>
                )}
              </GeneralFeedbackRight>
            )}
          </GeneralFeedbackContainer>
        )}
        {interview?.questions && (
          <QuestionPanel
            questions={interview?.questions}
            interviewId={interviewId}
            interview={interview}
            isUserPremium={isPremium}
          />
        )}
      </Content>
    </Container>
  )
}
